// Wire shapes → view models, and the one place RAG tiers are attached.
//
// The API deliberately returns raw indicator values with no tiering, so the
// provisional bands in ./methodology stay the single source of truth (PEAK-185).
// Everything a view renders as green/amber/red is decided here.

import { METRIC_DEFINITIONS, RAG_RANGES, tierForMetric, tierForScore, worstTier } from './methodology';
import type { ApiCohortDaily, ApiCohortRow, ApiFatigueSummary, ApiIndividuals } from './fatigueApi';
import type {
  CohortDailyPoint,
  CohortFatigueSummary,
  DerivedMetricKey,
  FatigueDerivedMetric,
  FatigueSummary,
  FatigueTrendPoint,
  IndividualFatigueRisk,
  TrendDirection,
} from './types';

const METRIC_KEYS: DerivedMetricKey[] = ['fed', 'lrm', 'cdi', 'pdc'];

/**
 * 0–100 normalisation for rings and gauges. FED is a rate per 1,000 workdays so
 * it needs scaling; the other three are already on a 0–100-ish scale.
 */
const fillFor = (key: DerivedMetricKey, value: number): number =>
  key === 'fed' ? Math.min(100, Math.round(value / 2)) : Math.min(100, Math.round(value));

/** Change is only a direction once it clears the noise floor for that metric. */
const NOISE_FLOOR: Record<DerivedMetricKey, number> = { fed: 5, lrm: 2, cdi: 2, pdc: 2 };

const directionFor = (key: DerivedMetricKey, delta: number): TrendDirection => {
  if (Math.abs(delta) < NOISE_FLOOR[key]) return 'stable';
  return delta > 0 ? 'up' : 'down';
};

const signed = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value)}`;

/**
 * Headline cards from the org values plus the measured trend.
 *
 * Both comparison labels come from the trend series rather than a separate
 * baseline call: the last fortnight against the one before it for the pill, and
 * against the oldest measured fortnight for the baseline line. Buckets the API
 * flagged as empty are skipped so a gap in coverage does not read as a drop to
 * zero.
 */
export const buildMetrics = (
  values: Record<DerivedMetricKey, number>,
  trend: FatigueTrendPoint[],
): FatigueDerivedMetric[] => {
  const measured = trend.filter((point) => !point.empty);
  const latest = measured[measured.length - 1];
  const previous = measured[measured.length - 2];
  const earliest = measured[0];

  return METRIC_KEYS.map((key) => {
    const def = METRIC_DEFINITIONS[key];
    const value = values[key];
    const fortnightDelta = latest && previous ? latest[key] - previous[key] : 0;
    const windowDelta = latest && earliest && earliest !== latest ? latest[key] - earliest[key] : 0;

    return {
      key,
      code: def.code,
      name: def.name,
      question: def.question,
      value,
      unit: RAG_RANGES[key].unit,
      fill: fillFor(key, value),
      trend: directionFor(key, fortnightDelta),
      trendLabel: previous ? `${signed(fortnightDelta)} vs last fortnight` : 'No prior period',
      baselineLabel:
        earliest && earliest !== latest
          ? `${signed(windowDelta)} vs ${earliest.period.replace('Wk-', '')} weeks ago`
          : 'No earlier history',
      goodDirection: 'down',
      riskTier: tierForMetric(key, value),
    };
  });
};

const mapCohort = (row: ApiCohortRow): CohortFatigueSummary => {
  // A cohort takes the worst tier of its four indicators. A suppressed or
  // empty cohort has no values to tier, so it reads green rather than being
  // coloured off zeros it never reported.
  const riskTier =
    row.suppressed || row.noData
      ? 'green'
      : worstTier([
          tierForMetric('fed', row.fedPer1000),
          tierForMetric('lrm', row.lrmPct),
          tierForMetric('cdi', row.cdiScore),
          tierForMetric('pdc', row.pdcPct),
        ]);

  return {
    cohort: { ...row.cohort, riskTier },
    fedPer1000: row.fedPer1000,
    fedPctWorkdays: row.fedPctWorkdays,
    lrmPct: row.lrmPct,
    cdiScore: row.cdiScore,
    pdcPct: row.pdcPct,
    trend: row.trend,
    riskTier,
    sparkline: row.sparkline,
    compliance: row.compliance,
    suppressed: row.suppressed,
    noData: row.noData,
  };
};

export const mapSummary = (api: ApiFatigueSummary): FatigueSummary => ({
  window: api.window,
  metrics: buildMetrics(api.metrics, api.trend),
  cohorts: api.cohorts.map(mapCohort),
  coverage: api.coverage,
  trend: api.trend,
  caveats: api.caveats,
  empty: api.empty,
});

export const mapCohortDaily = (api: ApiCohortDaily): CohortDailyPoint[] => api.series;

export const mapIndividuals = (api: ApiIndividuals): IndividualFatigueRisk[] =>
  api.individuals.map((person) => ({
    id: person.userId,
    name: person.name,
    jobRole: person.jobRole,
    fatigueScore: person.score,
    riskTier: tierForScore(person.score),
    dayCount: person.dayCount,
    fedPct: person.fedPct,
    lrmPct: person.lrmPct,
    cdiScore: person.cdiScore,
    inPdc: person.inPdc,
    avgSleepHours: person.avgSleepHours,
    avgReadiness: person.avgReadiness,
    avgStrain: person.avgStrain,
    // Per-person confidence is simply how much of the window they wore a device
    // for. The cohort figure blends coverage and completeness; for one person
    // coverage is either 0 or 100, so only completeness says anything.
    dataConfidence: api.window.days ? Math.round((person.dayCount / api.window.days) * 100) : 0,
  }));

// PEAK-263: site/shift-type/data-quality narrowing used to happen here,
// client-side, against the server's unfiltered cohort set. All of that now
// happens server-side instead — region/depot/department/job-role/age/sex/
// shift-pattern/reliable-data-only are sent to `/admin/fatigue/summary` the
// same way every other dashboard sends them, so `summary.cohorts`/`metrics`/
// `coverage` are already correct for the filtered population and need no
// client-side re-aggregation.
